import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Trash2, MessageSquare, Mail, Eye } from "lucide-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { toast } from "sonner"
import { Skeleton } from "@/components/ui/skeleton"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { format } from "date-fns"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatLead {
  id: string
  publisher_id: string
  name: string
  email: string
  phone: string | null
  interest: string
  chat_history: ChatMessage[]
  created_at: string
  updated_at: string
}

interface ContactInfo {
  name: string
  email: string
  phone: string
  company?: string
  message: string
  subject: string
}

interface ContactFormLead {
  id: string
  npo_id: string
  contact_info: ContactInfo
  status: string
  created_at: string
  updated_at: string
}

interface ChatLeadsResponse {
  data: ChatLead[]
  message: string
}

interface ContactFormLeadsResponse {
  data: ContactFormLead[]
}

export default function ChatLeadsPage({ profile, session }: { profile: any | null, session: any | null }) {
  const queryClient = useQueryClient()
  const [selectedLead, setSelectedLead] = useState<string | null>(null)
  const [selectedChat, setSelectedChat] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'chat' | 'contact'>('chat')

  const toTitleCase = (str: string) => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const { data: chatLeadsResponse, isLoading: chatLeadsLoading } = useQuery<ChatLeadsResponse>({
    queryKey: ["chat-leads"],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/tools/chat/leads`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )
      if (!response.ok) {
        throw new Error("Failed to fetch leads")
      }
      return response.json()
    },
    enabled: !!session?.access_token,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/tools/chat/leads/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )
      if (!response.ok) {
        throw new Error('Failed to delete lead')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-leads'] })
      toast.success('Lead deleted successfully')
    },
    onError: (error) => {
      console.error('Error deleting lead:', error)
      toast.error('Failed to delete lead')
    },
  })

  const handleDelete = async (id: string) => {
    setSelectedLead(null)
    await deleteMutation.mutateAsync(id)
  }

  const { data: contactLeadsResponse, isLoading: contactLeadsLoading } = useQuery<ContactFormLeadsResponse>({
    queryKey: ["contact-leads"],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/contacts/npo`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      )
      if (!response.ok) {
        throw new Error("Failed to fetch contact leads")
      }
      return response.json()
    },
    enabled: !!session?.access_token,
  })

  if (chatLeadsLoading || contactLeadsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  const chatLeads = chatLeadsResponse?.data || []
  const contactLeads = contactLeadsResponse?.data || []

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Leads Management</h1>
      
      <Tabs defaultValue="chat" className="w-full">
        <TabsList className="grid w-[400px] grid-cols-2">
          <TabsTrigger value="chat">Chat Leads</TabsTrigger>
          <TabsTrigger value="contact">Contact Form Leads</TabsTrigger>
        </TabsList>
        
        <TabsContent value="chat" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Chat Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Chat History</TableHead>
                    <TableHead className="w-[100px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chatLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>{lead.name}</TableCell>
                      <TableCell>{lead.email}</TableCell>
                      <TableCell>{lead.phone || "N/A"}</TableCell>
                      <TableCell>{lead.interest}</TableCell>
                      <TableCell>{format(new Date(lead.created_at), "PPp")}</TableCell>
                      <TableCell>
                        <Dialog open={selectedChat === lead.id} onOpenChange={(open) => !open && setSelectedChat(null)}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0"
                              onClick={() => setSelectedChat(lead.id)}
                            >
                              <MessageSquare className="h-4 w-4 mr-2" />
                              View Chat
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Chat History with {lead.name}</DialogTitle>
                            </DialogHeader>
                            <ScrollArea className="h-[500px] pr-4">
                              <div className="space-y-4">
                                {lead.chat_history.map((msg, idx) => (
                                  <div
                                    key={idx}
                                    className={`flex ${
                                      msg.role === "user" ? "justify-end" : "justify-start"
                                    }`}
                                  >
                                    <div
                                      className={`max-w-[80%] rounded-lg px-4 py-2 ${
                                        msg.role === "user"
                                          ? "bg-primary text-primary-foreground"
                                          : "bg-muted"
                                      }`}
                                    >
                                      {msg.content}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                      <TableCell className="text-center">
                        <AlertDialog open={selectedLead === lead.id} onOpenChange={(open) => !open && setSelectedLead(null)}>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setSelectedLead(lead.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Lead</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this lead? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-500 hover:bg-red-600"
                                onClick={() => handleDelete(lead.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="contact" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Form Leads</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[100px] text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contactLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>{lead.contact_info.name}</TableCell>
                      <TableCell>{lead.contact_info.email}</TableCell>
                      <TableCell>{lead.contact_info.phone || "N/A"}</TableCell>
                      <TableCell>{lead.contact_info.company || "N/A"}</TableCell>
                      <TableCell>{lead.contact_info.subject}</TableCell>
                      <TableCell>{format(new Date(lead.created_at), "PPp")}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.status === "pending" 
                            ? "bg-yellow-100 text-yellow-800" 
                            : lead.status === "contacted" 
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                        }`}>
                          {toTitleCase(lead.status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                            >
                              <Eye className="h-4 w-4 text-blue-500" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Message from {lead.contact_info.name}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Name</p>
                                  <p>{lead.contact_info.name}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Email</p>
                                  <p>{lead.contact_info.email}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Phone</p>
                                  <p>{lead.contact_info.phone || "N/A"}</p>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-500">Company</p>
                                  <p>{lead.contact_info.company || "N/A"}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-500">Subject</p>
                                <p>{lead.contact_info.subject}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-500">Message</p>
                                <p className="whitespace-pre-wrap">{lead.contact_info.message}</p>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
